/**
 * What a patient bought: a single session, or a package of them paid for up front.
 *
 * Called a purchase rather than a "patient service" because "service" already means
 * three things on this side too -- the bookable list, the Our Services price list, and
 * the API service classes. The labels a therapist reads still say services and packages.
 *
 * Everything here is a snapshot taken when the sale was made. The clinic can edit or
 * reprice a service afterwards and none of it changes what somebody already paid.
 */

import { IAppointment } from './appointment';
import { ServiceType } from './service';

/**
 * Where a purchase stands. Derived on the server from the row and its recorded sessions,
 * so "is this still usable" has one definition rather than one per screen.
 */
export type PurchaseStatus = 'ACTIVE' | 'COMPLETED' | 'EXPIRED' | 'CANCELLED';

/** One purchase, as a card renders it. The session history is fetched separately. */
export interface IPatientPurchase {
    id: number;
    patientId: number;
    /** Who provides this service -- not necessarily who sold it. */
    therapistId: number;
    therapistName: string;
    /** The therapist's picture, so the booking form can put a face beside the name. */
    therapistPictureUrl?: string | null;
    /** What was sold, in both languages. See IAppointment for why it is not resolved here. */
    serviceNameRo: string;
    /** Absent whenever the clinic gave the service no English name, which is usual. */
    serviceNameEn?: string | null;
    serviceType: ServiceType;
    durationMinutes: number;
    totalSessions: number;
    /**
     * Sessions given before the clinic moved onto Carefull, so with no appointment behind
     * them. 0 for anything sold here; set by an administrator bringing a package across.
     */
    sessionsUsedBefore: number;
    /**
     * `totalSessions` less the appointments actually *held*, and less `sessionsUsedBefore`.
     * Never negative.
     *
     * Held only: this is what the clinic still owes work for, so a booking that has not
     * happened does not reduce it. How many of the remainder are already in the diary is
     * `scheduledSessions`, and a card wants both — "2 left, 1 booked".
     */
    remainingSessions: number;
    /** How many of `remainingSessions` already have an appointment booked. */
    scheduledSessions: number;
    status: PurchaseStatus;
    /**
     * What the catalogue charged on the day of the sale. `null` on a patient's own view of
     * their purchase -- the API leaves the money out for that caller, the same way it
     * leaves out `notes`. Always present for staff.
     */
    listPrice: number | null;
    discountAmount: number | null;
    /** What the patient pays: `listPrice - discountAmount`. See `listPrice` on nullability. */
    price: number | null;
    /**
     * The catalogue option this was sold from, or null.
     *
     * The one field here that is not a snapshot, and the only reliable way back to the
     * priced line -- which the booking wizard needs so it can ask who else practises the
     * discipline it belongs to. Matching on the name and the duration instead was the old
     * way and it stops working the moment a clinic retimes the line, because this side of
     * the pair is frozen at the sale and the other is not.
     *
     * Null on rows older than the field, and it may name a line the clinic has since
     * dropped. Both mean the same thing to a caller: no colleagues to list.
     */
    sourceCatalogOptionId?: number | null;
    /**
     * Whether `therapistId` still gives sessions at this clinic.
     *
     * False means there is nobody to lead with -- they have left, or the sale was recorded
     * by somebody who does not treat patients, since the API defaults this to whoever
     * recorded it. `therapistName` is reported either way, because a card describing a
     * past sale should still say who sold it.
     */
    therapistActive: boolean;
    /** ISO date. */
    assignedOn: string;
    validUntil?: string | null;
    notes?: string | null;
}

/**
 * One purchase opened: the card plus every appointment against it, oldest first.
 *
 * Every appointment, not only the held ones. A no-show and a cancellation are part of how
 * a course of treatment actually went, and hiding them would make a package that took
 * twice as long as it should look uneventful.
 */
export interface IPurchaseDetail {
    purchase: IPatientPurchase;
    appointments: IAppointment[];
}

/**
 * What POST /patient-purchases takes.
 *
 * There is no `price`: the server computes it as `listPrice - discountAmount`. Sending
 * a total that did not follow from the discount beside it is not a thing the API allows,
 * which is why the dialog's live total is display only.
 *
 * Nor is there a list of appointments the sale settles. A session names what pays for it
 * as it is marked held, and a mis-named one is corrected with
 * `AppointmentsService.linkPurchaseMutation` — one way to write that link, not two.
 */
export interface CreatePurchaseRequest {
    patientId: number;
    serviceId: number;
    /**
     * Who will actually give the sessions. Sent explicitly rather than left to the
     * server's own default-to-caller fallback: that fallback is right for a therapist
     * selling their own patient a package, but wrong for an admin recording a sale on
     * somebody else's behalf.
     */
    therapistId?: number;
    assignedOn?: string;
    /**
     * "13:30:00" — the hour a single session was given, when it was given as it was sold.
     *
     * Set, the sale also writes the session into the diary: an appointment already held,
     * on `assignedOn` at this time, for the service's own length. Left out, the sale is
     * only a sale — which is what a session already booked in needs, since that
     * appointment exists and will name this purchase when it is marked held.
     *
     * A time and not a date-time: the day is `assignedOn`, and sending it twice would let
     * the two disagree. The API refuses it on a package.
     */
    providedAt?: string;
    totalSessions?: number;
    discountAmount?: number;
    listPrice?: number;
    validUntil?: string;
    notes?: string;
}

/** Sessions left, as a fraction of the whole -- what the progress bar shows. */
export const sessionsUsed = (purchase: IPatientPurchase): number =>
    purchase.totalSessions - purchase.remainingSessions;

/** Whether a purchase can still have a session recorded against it. */
export const isUsable = (purchase: IPatientPurchase): boolean =>
    purchase.status === 'ACTIVE' || purchase.status === 'EXPIRED';

/**
 * How many more appointments can be booked against this purchase.
 *
 * The remainder less what is already in the diary. Distinct from `remainingSessions`,
 * which is the money's question — this is the one the booking form has to ask, or a
 * patient with one session left books it twice.
 */
export const bookableSessions = (purchase: IPatientPurchase): number =>
    Math.max(purchase.remainingSessions - purchase.scheduledSessions, 0);

/** `PUT /patient-purchases/{id}/sessions-used-before`. The field name is the server's. */
export interface SetSessionsUsedBeforeRequest {
    sessionsUsedBefore: number;
}
